import React, { useState } from 'react'
import { useGetMessageAppearance } from '../hooks/useGetMessageAppearance'
import { useMessageAppearanceAckV2 } from '../hooks/useMessageAppearanceAckV2'
import { getAckTimestampForDelay } from '../ackTimestamp'
import { useParams } from 'react-router'
import { Alert } from '@material-ui/lab'
import MuiAlert from '@material-ui/lab/Alert'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import { BoardPreview, FlexHorizontal, IBoard, Spacer } from '@vestaboard/installables'
import { TruncatedId } from '../../truncated-id'
import { FormattedDateTime } from '../../formatted-date-time'
import { BoardLastAcknowledgment } from '../../shared/BoardLastAcknowledgment'
import { useBoardDetailQuery } from '../../board/hooks/useBoardDetailQuery'
import { useEnableDeviceAcks, useDisableDeviceAcks } from '../../../api/useDeviceAcks'

const useStyles = makeStyles(theme => ({
  cardInner: {
    padding: 20
  },
  detailLabelCell: {
    width: 200,
    verticalAlign: 'top',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  detailValueCell: {
    verticalAlign: 'top',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  }
}))

const DetailRow: React.FC<{
  label: string
  labelClass: string
  valueClass: string
  children: React.ReactNode
}> = ({ label, labelClass, valueClass, children }) => (
  <TableRow>
    <TableCell className={labelClass} component='th' scope='row'>
      <strong>{label}</strong>
    </TableCell>
    <TableCell className={valueClass}>{children}</TableCell>
  </TableRow>
)

export const GetMessageAppearance = () => {
  const classes = useStyles()
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useGetMessageAppearance(id)
  const { data: ackData, loading: ackLoading } = useMessageAppearanceAckV2(id)
  const [enableAcks] = useEnableDeviceAcks()
  const [disableAcks] = useDisableDeviceAcks()
  const [ackActionLoading, setAckActionLoading] = useState(false)
  const [ackActionMessage, setAckActionMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null)

  const boardId = data?.messageAppearance?.__typename === 'MessageAppearanceV2' ? data.messageAppearance.boardId : ''
  const { data: boardData } = useBoardDetailQuery(boardId)
  const devices = boardData?.board?.devices ?? []

  const appearanceForAck =
    data?.messageAppearance?.__typename === 'MessageAppearanceV2' ? data.messageAppearance : undefined
  const ackRecord = ackData?.messageAppearanceAckV2 ?? undefined
  const acknowledgmentText = ackLoading ? '…' : ackRecord ? 'true' : 'false'
  const acknowledgmentDelayText = ackLoading
    ? '…'
    : !ackRecord || !appearanceForAck
    ? '—'
    : `${Math.round(getAckTimestampForDelay(ackRecord) - appearanceForAck.sentAt)} ms`

  const handleAckAction = async (enable: boolean) => {
    if (devices.length === 0) return
    setAckActionLoading(true)
    const mutation = enable ? enableAcks : disableAcks
    let successCount = 0
    let failCount = 0
    for (const device of devices) {
      try {
        await mutation({ variables: { device: device.id } })
        successCount++
      } catch {
        failCount++
      }
    }
    setAckActionLoading(false)
    if (failCount === 0) {
      setAckActionMessage({ text: `Acks ${enable ? 'enabled' : 'disabled'} on ${successCount} device(s)`, severity: 'success' })
    } else {
      setAckActionMessage({ text: `${successCount} succeeded, ${failCount} failed`, severity: 'error' })
    }
  }

  return error ? (
    <Alert severity='error'>There was an error loading the message appearance</Alert>
  ) : loading ? (
    <CircularProgress />
  ) : data?.messageAppearance.__typename === 'MessageAppearanceErrorV2' ? (
    <Alert severity='error'>{data.messageAppearance.error}</Alert>
  ) : (
    <>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Message Appearance</Typography>
      </FlexHorizontal>
      <Spacer size='large' />
      <Card>
        <Box className={classes.cardInner}>
          <TextField value={data?.messageAppearance.id} disabled label='ID' variant='outlined' fullWidth />
          <Spacer size='large' />
          <TextField value={data?.messageAppearance.message.text} disabled label='Text' variant='outlined' fullWidth />
          <Spacer size='large' />
          <Box width={400}>
            <BoardPreview characters={data?.messageAppearance.message?.characters as IBoard} />
          </Box>
          <Spacer size='large' />
          <Table size='small'>
            <TableBody>
              <DetailRow label='Message ID' labelClass={classes.detailLabelCell} valueClass={classes.detailValueCell}>
                <TruncatedId
                  value={data?.messageAppearance.message.id || ''}
                  path={`/message/${data?.messageAppearance.message.id}`}
                  full
                />
              </DetailRow>
              <DetailRow label='Board ID' labelClass={classes.detailLabelCell} valueClass={classes.detailValueCell}>
                <TruncatedId
                  value={data?.messageAppearance.boardId || ''}
                  path={`/boards/${data?.messageAppearance.boardId}`}
                  full
                />
              </DetailRow>
              <DetailRow label='Date' labelClass={classes.detailLabelCell} valueClass={classes.detailValueCell}>
                <FormattedDateTime value={data?.messageAppearance.created} />
              </DetailRow>
              <DetailRow label='Attribution' labelClass={classes.detailLabelCell} valueClass={classes.detailValueCell}>
                <TruncatedId
                  value={data?.messageAppearance.attribution || ''}
                  path={
                    data?.messageAppearance.personId
                      ? `/person/${data?.messageAppearance.personId}`
                      : `/subscription/${data?.messageAppearance.subscriptionId}`
                  }
                  full
                />
              </DetailRow>
              <DetailRow
                label='Device Message Acknowledgment'
                labelClass={classes.detailLabelCell}
                valueClass={classes.detailValueCell}>
                {acknowledgmentText}
              </DetailRow>
              <DetailRow
                label='Delay From Send to Device Acknowledgment'
                labelClass={classes.detailLabelCell}
                valueClass={classes.detailValueCell}>
                {acknowledgmentDelayText}
              </DetailRow>
              <DetailRow
                label='Last Device Message Acknowledgment'
                labelClass={classes.detailLabelCell}
                valueClass={classes.detailValueCell}>
                <BoardLastAcknowledgment boardId={data?.messageAppearance.boardId || ''} />
              </DetailRow>
              <DetailRow
                label='Manage Device Acks'
                labelClass={classes.detailLabelCell}
                valueClass={classes.detailValueCell}>
                <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Tooltip title='The ack state is managed on device' arrow>
                    <span>
                      <Button
                        variant='outlined'
                        size='small'
                        disabled={devices.length === 0 || ackActionLoading}
                        onClick={() => handleAckAction(true)}>
                        {ackActionLoading ? <CircularProgress size={18} /> : 'Enable Acks'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title='The ack state is managed on device' arrow>
                    <span>
                      <Button
                        variant='outlined'
                        size='small'
                        disabled={devices.length === 0 || ackActionLoading}
                        onClick={() => handleAckAction(false)}>
                        {ackActionLoading ? <CircularProgress size={18} /> : 'Disable Acks'}
                      </Button>
                    </span>
                  </Tooltip>
                  {devices.length === 0 && (
                    <Typography variant='caption' color='textSecondary'>
                      No devices paired to this board
                    </Typography>
                  )}
                </Box>
              </DetailRow>
            </TableBody>
          </Table>
        </Box>
      </Card>
      <Snackbar open={!!ackActionMessage} autoHideDuration={4000} onClose={() => setAckActionMessage(null)}>
        <MuiAlert onClose={() => setAckActionMessage(null)} severity={ackActionMessage?.severity}>
          {ackActionMessage?.text}
        </MuiAlert>
      </Snackbar>
    </>
  )
}
